import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService from '@services/BrandingsService';
import type { IBrandingEntityValues } from '@interfaces/rooms';

import type {
  BrandingCsvSkipReason,
  IBrandingCsvImportPlan,
} from '../brandingEntitiesCsv';
import { sendBrandingEntitiesUpdatedToUnreal } from '../brandingSpotsUnreal';
import { useEditorMode } from '../EditorModeContext';
import { useInspectorStyles } from '../Inspector/styles';

/** How many skipped rows are listed before the rest are summarized as a count. */
const MAX_LISTED_SKIPPED_ROWS = 20;

const SKIP_REASON_MESSAGES: Record<
  BrandingCsvSkipReason,
  { key: string; fallback: string }
> = {
  malformedRow: {
    key: 'editorMode.objectsCsvSkipMalformed',
    fallback: 'The row has no object or property id.',
  },
  unknownEntity: {
    key: 'editorMode.objectsCsvSkipUnknownEntity',
    fallback: 'This build declares no such object.',
  },
  unknownProperty: {
    key: 'editorMode.objectsCsvSkipUnknownProperty',
    fallback: 'The object declares no such property.',
  },
  invalidValue: {
    key: 'editorMode.objectsCsvSkipInvalidValue',
    fallback: 'The value does not match the property type.',
  },
  duplicateRow: {
    key: 'editorMode.objectsCsvSkipDuplicate',
    fallback: 'A later row sets the same property; that one is used.',
  },
};

/**
 * Dialog buttons: an outline on hover marks what is clickable, and a disabled
 * one is muted rather than merely inert — a full-strength label on a button
 * that does nothing reads as a broken button.
 */
const DIALOG_BUTTON_SX = {
  textTransform: 'none',
  borderRadius: '8px',
  border: '1px solid transparent',
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  // MUI stops pointer events on a disabled button, so this cannot fire there.
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.28)',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
  },
  '&.Mui-disabled': {
    color: 'rgba(226, 228, 237, 0.32)',
    borderColor: 'transparent',
  },
};

const FILE_ERROR_MESSAGES = {
  empty: {
    key: 'editorMode.objectsCsvErrorEmpty',
    fallback: 'The file has no rows.',
  },
  missingColumns: {
    key: 'editorMode.objectsCsvErrorColumns',
    fallback:
      'The header must carry the entityId, propertyId and value columns.',
  },
};

interface IObjectsCsvImportModalProps {
  open: boolean;
  plan: IBrandingCsvImportPlan | null;
  brandingId: string | null;
  isEditingLive: boolean;
  onClose: () => void;
  onImported: () => void;
}

/**
 * Confirms what an object CSV would change before any of it is written.
 *
 * Nothing reaches the backend until the user accepts: the file is parsed into a
 * plan, the plan is shown with every row it had to drop, and only then is the
 * whole set sent as one request.
 *
 * @param props.open - Whether the dialog is shown.
 * @param props.plan - Parsed file; `null` renders nothing.
 * @param props.brandingId - Configuration the values are written to.
 * @param props.isEditingLive - Whether that configuration is the room's live one.
 * @param props.onClose - Called when the dialog is dismissed or finished.
 * @param props.onImported - Called after the values have been stored.
 */
const ObjectsCsvImportModal = ({
  open,
  plan,
  brandingId,
  isEditingLive,
  onClose,
  onImported,
}: IObjectsCsvImportModalProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const { sendMessageToUnreal } = useMytaverse();
  const { setAutosaveStatus, flushBrandingEntitySave } = useEditorMode();

  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  if (!plan) {
    return null;
  }

  const hasChanges = plan.appliedCount > 0;
  const canApply = !plan.fileError && hasChanges && !!brandingId;

  /**
   * Writes the planned values and tells Unreal what was stored.
   */
  const handleApply = async () => {
    if (!brandingId) {
      return;
    }

    setIsImporting(true);
    setImportError(null);
    setAutosaveStatus('saving');

    try {
      // The inspector may still be holding an edit to one of these entities;
      // letting it land first keeps the two writes in a defined order.
      await flushBrandingEntitySave();

      const updated = await BrandingsService.updateBranding(brandingId, {
        brandingEntities: plan.entities,
      });

      // Announce what the backend stored, not what was sent — an uploaded
      // texture is relocated as it is persisted, and an entity whose last
      // override was cleared comes back absent and has to be sent as empty so
      // Unreal restores the authored values.
      const appliedEntities: IBrandingEntityValues = {};
      Object.keys(plan.entities).forEach((entityId) => {
        appliedEntities[entityId] = updated?.brandingEntities?.[entityId] ?? {};
      });

      sendBrandingEntitiesUpdatedToUnreal(sendMessageToUnreal, appliedEntities);

      setAutosaveStatus('saved');
      onImported();
      onClose();
    } catch (saveError) {
      console.error(
        `Failed to import branding entity values: ${
          (saveError as Error)?.message
        }`,
        { brandingId },
      );
      setAutosaveStatus('error');
      setImportError(
        translate(
          'editorMode.objectsCsvImportFailed',
          'Import failed. Nothing was saved — try again.',
        ),
      );
    } finally {
      setIsImporting(false);
    }
  };

  const listedChanges = plan.changes.slice(0, MAX_LISTED_SKIPPED_ROWS);
  const hiddenChangeCount = plan.changes.length - listedChanges.length;
  // Both sides of a change can be empty, which is not nothing: it is the value
  // the level authored, before or after the override.
  const authoredLabel = translate(
    'editorMode.objectsCsvChangeAuthored',
    'level default',
  );
  const listedSkipped = plan.skipped.slice(0, MAX_LISTED_SKIPPED_ROWS);
  const hiddenSkippedCount = plan.skipped.length - listedSkipped.length;
  const listedReferenceEdits = plan.referenceEdits.slice(
    0,
    MAX_LISTED_SKIPPED_ROWS,
  );
  const hiddenReferenceEditCount =
    plan.referenceEdits.length - listedReferenceEdits.length;

  return (
    <Dialog
      open={open}
      onClose={isImporting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid="objects-csv-import-modal"
      PaperProps={{
        sx: {
          background:
            'linear-gradient(135deg, rgba(28, 28, 34, 0.94) 0%, rgba(16, 16, 20, 0.94) 100%)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
          borderRadius: '16px',
          color: '#e2e4ed',
          '& .MuiDialogTitle-root': { color: '#e2e4ed', fontSize: '16px' },
          // Left to the button's own sx when disabled, which mutes it.
          '& .MuiButton-text:not(.Mui-disabled)': { color: '#e2e4ed' },
        },
      }}
    >
      <DialogTitle>
        {translate('editorMode.objectsCsvImportTitle', 'Import objects')}
      </DialogTitle>

      <DialogContent>
        {plan.fileError ? (
          <Typography sx={styles.helperError}>
            {translate(
              FILE_ERROR_MESSAGES[plan.fileError].key,
              FILE_ERROR_MESSAGES[plan.fileError].fallback,
            )}
          </Typography>
        ) : (
          <>
            {isEditingLive && (
              <Box sx={styles.liveWarning}>
                <WarningAmberOutlinedIcon
                  sx={{ fontSize: 14, color: '#FFB800' }}
                />
                <Typography sx={styles.liveWarningText}>
                  {translate('editorMode.liveBrandingWarning')}
                </Typography>
              </Box>
            )}

            <Typography
              sx={styles.brandingEntityMetaText}
              data-testid="objects-csv-import-summary"
            >
              {hasChanges
                ? translate(
                    'editorMode.objectsCsvImportSummary',
                    '{{values}} values across {{objects}} objects will change.',
                    {
                      values: plan.appliedCount,
                      objects: plan.changedEntityIds.length,
                    },
                  )
                : translate(
                    'editorMode.objectsCsvImportNoChanges',
                    'No value in this file differs from what is saved, so there is nothing to import.',
                  )}
            </Typography>

            {plan.changes.length > 0 && (
              <Box sx={styles.section} data-testid="objects-csv-import-changes">
                <Typography sx={styles.sectionLabel}>
                  {translate('editorMode.objectsCsvChangesTitle', 'Changes')}
                </Typography>
                {listedChanges.map((change) => (
                  <Typography
                    key={`${change.entityId}-${change.propertyId}`}
                    sx={styles.brandingPropertyHint}
                    data-testid={`objects-csv-import-change-${change.entityId}-${change.propertyId}`}
                  >
                    {translate(
                      'editorMode.objectsCsvChangesRow',
                      '{{entityId}} / {{propertyId}}: {{from}} → {{to}}',
                      {
                        entityId: change.entityId,
                        propertyId: change.propertyId,
                        from: change.from || authoredLabel,
                        to: change.to || authoredLabel,
                      },
                    )}
                  </Typography>
                ))}
                {hiddenChangeCount > 0 && (
                  <Typography sx={styles.brandingPropertyHint}>
                    {translate(
                      'editorMode.objectsCsvSkippedMore',
                      'and {{count}} more.',
                      { count: hiddenChangeCount },
                    )}
                  </Typography>
                )}
              </Box>
            )}

            {plan.referenceEdits.length > 0 && (
              <Box sx={styles.section}>
                <Box
                  sx={styles.liveWarning}
                  data-testid="objects-csv-import-reference-edits"
                >
                  <WarningAmberOutlinedIcon
                    sx={{ fontSize: 14, color: '#FFB800' }}
                  />
                  <Typography sx={styles.liveWarningText}>
                    {translate(
                      'editorMode.objectsCsvReferenceEditsTitle',
                      'Edited in a column the import does not read ({{count}}):',
                      { count: plan.referenceEdits.length },
                    )}
                  </Typography>
                </Box>
                {listedReferenceEdits.map((edit) => (
                  <Typography
                    key={`${edit.line}-${edit.columns.join('-')}`}
                    sx={styles.brandingPropertyHint}
                    data-testid={`objects-csv-import-reference-edit-${edit.line}`}
                  >
                    {translate(
                      'editorMode.objectsCsvReferenceEditsRow',
                      'Line {{line}} — {{entityId}} / {{propertyId}}: {{columns}}',
                      {
                        line: edit.line,
                        entityId: edit.entityId,
                        propertyId: edit.propertyId,
                        columns: edit.columns.join(', '),
                      },
                    )}
                  </Typography>
                ))}
                {hiddenReferenceEditCount > 0 && (
                  <Typography sx={styles.brandingPropertyHint}>
                    {translate(
                      'editorMode.objectsCsvSkippedMore',
                      'and {{count}} more.',
                      { count: hiddenReferenceEditCount },
                    )}
                  </Typography>
                )}
                <Typography sx={styles.brandingPropertyHint}>
                  {translate(
                    'editorMode.objectsCsvReferenceEditsHint',
                    'Those columns come from the build, so the import leaves them alone. Put the change in the value column instead.',
                  )}
                </Typography>
              </Box>
            )}

            {plan.foreignAssetUrls.length > 0 && (
              <Box
                sx={styles.liveWarning}
                data-testid="objects-csv-import-foreign-assets"
              >
                <WarningAmberOutlinedIcon
                  sx={{ fontSize: 14, color: '#FFB800' }}
                />
                <Typography sx={styles.liveWarningText}>
                  {translate(
                    'editorMode.objectsCsvForeignAssets',
                    '{{count}} values point at assets outside this environment. They are imported as they are, but the files are not copied, so they may not load.',
                    { count: plan.foreignAssetUrls.length },
                  )}
                </Typography>
              </Box>
            )}

            {plan.skipped.length > 0 && (
              <Box sx={styles.section}>
                <Typography sx={styles.sectionLabel}>
                  {translate(
                    'editorMode.objectsCsvSkippedTitle',
                    'Skipped rows ({{count}})',
                    { count: plan.skipped.length },
                  )}
                </Typography>
                {listedSkipped.map((skippedRow) => (
                  <Typography
                    key={`${skippedRow.line}-${skippedRow.propertyId}-${skippedRow.reason}`}
                    sx={styles.brandingPropertyHint}
                    data-testid={`objects-csv-import-skipped-row-${skippedRow.line}`}
                  >
                    {translate(
                      'editorMode.objectsCsvSkippedRow',
                      'Line {{line}} — {{entityId}} / {{propertyId}}: {{reason}}',
                      {
                        line: skippedRow.line,
                        entityId: skippedRow.entityId || '—',
                        propertyId: skippedRow.propertyId || '—',
                        reason: translate(
                          SKIP_REASON_MESSAGES[skippedRow.reason].key,
                          SKIP_REASON_MESSAGES[skippedRow.reason].fallback,
                        ),
                      },
                    )}
                  </Typography>
                ))}
                {hiddenSkippedCount > 0 && (
                  <Typography sx={styles.brandingPropertyHint}>
                    {translate(
                      'editorMode.objectsCsvSkippedMore',
                      'and {{count}} more.',
                      { count: hiddenSkippedCount },
                    )}
                  </Typography>
                )}
              </Box>
            )}

            {importError && (
              <Typography sx={styles.helperError}>{importError}</Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ gap: '8px' }}>
        {/* Says why the button next to it is dead. Without it the dialog offers
            an Import that silently refuses every click. */}
        {!canApply && !isImporting && (
          <Typography
            sx={{ ...styles.brandingPropertyHint, flex: 1, margin: 0 }}
            data-testid="objects-csv-import-disabled-hint"
          >
            {plan.fileError
              ? translate(
                  'editorMode.objectsCsvImportDisabledUnusable',
                  'Fix the file and pick it again.',
                )
              : translate(
                  'editorMode.objectsCsvImportDisabledNoChanges',
                  'Import needs at least one changed cell in the value column.',
                )}
          </Typography>
        )}
        <Button
          onClick={onClose}
          disabled={isImporting}
          sx={DIALOG_BUTTON_SX}
          data-testid="objects-csv-import-cancel-btn"
        >
          {translate('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleApply}
          disabled={!canApply || isImporting}
          sx={DIALOG_BUTTON_SX}
          data-testid="objects-csv-import-apply-btn"
        >
          {translate('editorMode.objectsCsvImportApply', 'Import')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ObjectsCsvImportModal;
